<template>
  <div class="order-detail">
    <el-card shadow="never">
      <div slot="header" class="card-header">
        <el-button type="text" icon="el-icon-arrow-left" @click="$router.back()">返回</el-button>
        <span class="title">订单详情</span>
      </div>

      <div v-loading="loading">
        <template v-if="detail && detail.id">
          <!-- 订单信息 -->
          <el-divider content-position="left">订单信息</el-divider>
          <el-descriptions :column="2" border size="small">
            <el-descriptions-item label="订单号">{{ detail.order_no }}</el-descriptions-item>
            <el-descriptions-item label="订单类型">{{ detail.goods_type_text }}</el-descriptions-item>
            <el-descriptions-item label="订单状态">
              <el-tag :type="statusTagType(detail.order_status)" size="small">{{ detail.order_status_text }}</el-tag>
            </el-descriptions-item>
            <el-descriptions-item label="支付方式">{{ detail.pay_type_text }}</el-descriptions-item>
            <el-descriptions-item label="订单金额">
              <span class="amount-text">&yen;{{ detail.total_amount }}</span>
            </el-descriptions-item>
            <el-descriptions-item label="订单PV">{{ detail.total_pv || 0 }}</el-descriptions-item>
            <el-descriptions-item label="下单时间">{{ detail.create_time }}</el-descriptions-item>
            <el-descriptions-item label="支付时间">{{ detail.pay_time || '—' }}</el-descriptions-item>
            <el-descriptions-item label="发货时间">{{ detail.delivery_time || '—' }}</el-descriptions-item>
            <el-descriptions-item label="确认收货">{{ detail.confirm_time || '—' }}</el-descriptions-item>
            <el-descriptions-item v-if="detail.branch_name" label="所属分身">{{ detail.branch_name }}</el-descriptions-item>
            <el-descriptions-item v-if="detail.puhui_admin_name" label="普惠管理员">{{ detail.puhui_admin_name }}</el-descriptions-item>
          </el-descriptions>

          <!-- 用户信息 -->
          <el-divider content-position="left">用户信息</el-divider>
          <el-descriptions :column="2" border size="small">
            <el-descriptions-item label="用户昵称">{{ detail.user_name }}</el-descriptions-item>
            <el-descriptions-item label="手机号">{{ detail.user_phone || '—' }}</el-descriptions-item>
            <el-descriptions-item label="收货方式">{{ detail.receive_type_text }}</el-descriptions-item>
            <el-descriptions-item label="商品来源">{{ detail.supplier_type_text }}</el-descriptions-item>
          </el-descriptions>

          <!-- 收货地址 -->
          <template v-if="detail.address_info">
            <el-divider content-position="left">收货地址</el-divider>
            <el-descriptions :column="2" border size="small">
              <el-descriptions-item label="收货人">{{ detail.address_info.name || '—' }}</el-descriptions-item>
              <el-descriptions-item label="联系电话">{{ detail.address_info.phone || '—' }}</el-descriptions-item>
              <el-descriptions-item label="收货地址" :span="2">
                {{ (detail.address_info.region || '') + (detail.address_info.address || '') || '—' }}
              </el-descriptions-item>
            </el-descriptions>
          </template>

          <!-- 物流信息 -->
          <template v-if="detail.logistics_company || detail.logistics_no">
            <el-divider content-position="left">物流信息</el-divider>
            <el-descriptions :column="2" border size="small">
              <el-descriptions-item label="物流公司">{{ detail.logistics_company || '—' }}</el-descriptions-item>
              <el-descriptions-item label="物流单号">{{ detail.logistics_no || '—' }}</el-descriptions-item>
            </el-descriptions>
          </template>

          <!-- 商品明细 -->
          <el-divider content-position="left">商品明细</el-divider>
          <el-table :data="detail.items" border size="small">
            <el-table-column label="商品" min-width="220">
              <template slot-scope="scope">
                <div class="goods-info">
                  <el-image v-if="scope.row.goods_img" :src="scope.row.goods_img" style="width:48px;height:48px;border-radius:4px" fit="cover" />
                  <div>
                    <div>{{ scope.row.goods_name }}</div>
                    <div v-if="scope.row.sku_spec_text" class="goods-spec">{{ scope.row.sku_spec_text }}</div>
                  </div>
                </div>
              </template>
            </el-table-column>
            <el-table-column prop="sell_price" label="单价" width="100">
              <template slot-scope="scope">&yen;{{ scope.row.sell_price }}</template>
            </el-table-column>
            <el-table-column prop="buy_num" label="数量" width="80" />
            <el-table-column prop="pv_gain" label="PV" width="80" />
            <el-table-column prop="sub_total" label="小计" width="100">
              <template slot-scope="scope">
                <span class="amount-text">&yen;{{ scope.row.sub_total }}</span>
              </template>
            </el-table-column>
          </el-table>

          <!-- 臻赏消费券解锁进度 -->
          <template v-if="detail.coupon_freeze">
            <el-divider content-position="left">臻赏消费券解锁进度</el-divider>
            <el-descriptions :column="2" border size="small">
              <el-descriptions-item label="赠送总额">
                <span class="amount-text">&yen;{{ detail.coupon_freeze.total_amount }}</span>
              </el-descriptions-item>
              <el-descriptions-item label="解锁状态">
                <el-tag :type="detail.coupon_freeze.status === 1 ? 'success' : 'warning'" size="small">{{ detail.coupon_freeze.status_text }}</el-tag>
              </el-descriptions-item>
              <el-descriptions-item label="释放天数">{{ detail.coupon_freeze.release_days }} 天</el-descriptions-item>
              <el-descriptions-item label="每日释放">&yen;{{ detail.coupon_freeze.daily_amount }}</el-descriptions-item>
              <el-descriptions-item label="已解锁天数">{{ detail.coupon_freeze.released_days }} 天</el-descriptions-item>
              <el-descriptions-item label="已解锁金额">&yen;{{ detail.coupon_freeze.released_amount }}</el-descriptions-item>
              <el-descriptions-item label="冻结中">&yen;{{ detail.coupon_freeze.frozen_amount }}</el-descriptions-item>
              <el-descriptions-item label="已解冻到余额">&yen;{{ detail.coupon_freeze.unfrozen_amount }}</el-descriptions-item>
            </el-descriptions>
          </template>

          <!-- 底部操作 -->
          <div style="text-align:center;margin-top:24px">
            <el-button type="danger" v-if="detail.order_status === 1" @click="doDeliver">发货</el-button>
            <el-button type="warning" v-if="detail.order_status === 4" @click="doRefund">处理退款</el-button>
            <el-button @click="$router.back()">返回</el-button>
          </div>
        </template>
        <el-empty v-else description="暂无数据" />
      </div>
    </el-card>
  </div>
</template>

<script>
import { getOrderDetail, orderShipment, orderRefund } from '@/api'

export default {
  name: 'OrderDetail',
  data() {
    return {
      detail: {},
      loading: false
    }
  },
  mounted() {
    this.loadDetail()
  },
  methods: {
    async loadDetail() {
      this.loading = true
      try {
        const orderNo = this.$route.params.orderNo
        this.detail = await getOrderDetail({ orderNo })
      } catch (e) {
        this.$message.error('获取订单详情失败')
      }
      this.loading = false
    },
    statusTagType(status) {
      const map = { 0: 'info', 1: 'danger', 2: '', 3: 'success', 4: 'warning', 5: 'warning', 6: 'info' }
      return map[status] || 'info'
    },
    doDeliver() {
      this.$confirm(`确认发货: ${this.detail.order_no}？`, '发货确认', { type: 'success' }).then(() => {
        orderShipment({ orderNo: this.detail.orderNo }).then(() => {
          this.$message.success('发货成功')
          this.loadDetail()
        }).catch(() => {
          this.$message.error('发货失败')
        })
      }).catch(() => {})
    },
    doRefund() {
      this.$confirm(`处理退款: ${this.detail.order_no}？`, '退款处理', { type: 'warning' }).then(() => {
        orderRefund({ orderNo: this.detail.orderNo }).then(() => {
          this.$message.success('退款处理完成')
          this.loadDetail()
        }).catch(() => {
          this.$message.error('退款处理失败')
        })
      }).catch(() => {})
    }
  }
}
</script>

<style scoped>
.card-header { display: flex; align-items: center; }
.card-header .title { font-size: 16px; font-weight: bold; margin-left: 12px; }
.amount-text { color: #3a8ee6; font-weight: bold; }
.goods-info { display: flex; align-items: center; gap: 10px; }
.goods-spec { font-size: 12px; color: #909399; }
</style>
